import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AssessmentCompletionMetric } from './metrics/AssessmentCompletionMetric';
import { SurveyInProgressWidget } from './metrics/SurveyInProgressWidget';
import { ScoresChart } from '../interventions/recommendations/ScoresChart';
import {
  ASSESSMENT_PERIOD_OPEN,
  ASSESSMENT_PERIOD_IN_PROGRESS,
  ASSESSMENT_PERIOD_SURVEY,
} from '@/areas/assessment-schedule/constants';
import { useDashboardCardsData } from './dashboard/DashboardCardsLayout';
import { useCultureHubQueryParams } from '../../hooks/useCultureHubQueryParams';
import { useGetCultureHubAnalyticsQuery } from '../../api/cultureHubApi';
import { ScoreGaugeCard } from './dashboard/ScoreGaugeCard';
import { isCurrentMemberTeamLeader, getMemberId } from '@/common/selectors/auth';
import { CardContent } from '@/common/ui/card';
import LoadingSpinner from '@/common/components/LoadingSpinner';
import { getAnalyticsSelectedPillar } from '../../slices';
import { useApiPortalV1AssessmentsResultsListQuery } from '@/areas/assessment-schedule/api';

import { AggregateType } from '@/common/constants/AggregateType';
interface AnalyticsTabProps {
  // Props removed in favor of hooks/selectors
}

// Shared styles
const CONTAINER_STYLE = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '20px',
  minHeight: 0,
} as const;

const CONTENT_STYLE = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'auto',
} as const;


// Shared wrapper component
const AnalyticsCardWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={CONTAINER_STYLE}>
    {children}
  </div>
);

export const AnalyticsTab: React.FC<AnalyticsTabProps> = () => {
  const { assessmentPeriodId, teamId, shouldFetchData, assessmentPeriodStatus } = useCultureHubQueryParams();
  const selectedPillar = useSelector(getAnalyticsSelectedPillar);
  const currentUserId = useSelector(getMemberId);

  const { data: analyticsData, isLoading, isSuccess, isFetching } = useGetCultureHubAnalyticsQuery(
    { assessmentPeriodId: assessmentPeriodId!, teamId },
    { skip: !assessmentPeriodId || !shouldFetchData }
  );

  const { data: ai_sentiment_data, isLoading: aiLoading, isFetching: aiFetching } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.TEAM_SUMMARY,
    assessmentPeriodId: assessmentPeriodId!,
    employeeId: currentUserId ?? undefined,
  },
    { skip: !assessmentPeriodId }

  );

  const { isLoading: scoreSentimentLoading, isFetching: scoreSentimentFetching } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.SENTIMENT_DISTRIBUTION,
    assessmentPeriodId: assessmentPeriodId!,
    employeeId: currentUserId ?? undefined,
  },
    { skip: !assessmentPeriodId }
  );

  const { aiSummary, teamName } = useMemo(() => {
    const summaries = ai_sentiment_data?.[0]?.result?.summaries;
    if (!summaries || !teamId) return { aiSummary: undefined, teamName: undefined };

    const found = summaries.find((s: any) => Number(s.team_id) === Number(teamId));
    if (!found) return { aiSummary: undefined, teamName: undefined };

    const teamName = found.team_name ?? undefined;
    const aiSummary = found.summary ? { summary: found.summary, bullets: found.bullets } : undefined;
    return { aiSummary, teamName };
  }, [ai_sentiment_data, teamId]);

  // Consider query fetching state as a 'loading' state so we don't render stale data while new data arrives
  const fetching = Boolean(
    isLoading || isFetching || aiLoading || aiFetching || scoreSentimentLoading || scoreSentimentFetching
  );

  // Get pillars from analytics endpoint - now in PillarData format
  const allPillars = useMemo(() => {
    if (!analyticsData?.pillars) return [];
    return Object.values(analyticsData.pillars);
  }, [analyticsData?.pillars]);

  // Filter pillars if a specific pillar is selected
  const filteredPillars = useMemo(() => {
    if (!selectedPillar) return allPillars;
    return allPillars.filter(p => p.name === selectedPillar);
  }, [selectedPillar, allPillars]);

  const { overallScore, differential, previousScore, isLoaded: cardsLoaded } = useDashboardCardsData();
  const isTeamLeader = useSelector(isCurrentMemberTeamLeader);

  const shouldShowCompletionMetric =
    assessmentPeriodStatus === ASSESSMENT_PERIOD_OPEN ||
    assessmentPeriodStatus === ASSESSMENT_PERIOD_IN_PROGRESS ||
    assessmentPeriodStatus === ASSESSMENT_PERIOD_SURVEY;

  // Only consider "no team data" when all team-scoped sections are empty.
  const hasTeamContent = useMemo(() => {
    if (!analyticsData) {
      return false;
    }

    const hasPillars = allPillars.length > 0;
    const hasPillarScores = (analyticsData.pillar_scores?.pillars?.length ?? 0) > 0;
    const hasOutcomeScores = (analyticsData.outcome_scores?.outcomes?.length ?? 0) > 0;
    const hasTeamSentiment = (analyticsData.sentiment_scores?.team_pillar_sentiments?.length ?? 0) > 0;
    const hasSummary = Boolean(analyticsData.ai_summary?.summary || aiSummary?.summary);

    return hasPillars || hasPillarScores || hasOutcomeScores || hasTeamSentiment || hasSummary;
  }, [analyticsData, allPillars.length, aiSummary?.summary]);

  const noTeamData = isSuccess && !hasTeamContent;

  // Show loading spinner while data is being fetched (initial load or refetch)
  if (fetching) {
    return (
      <AnalyticsCardWrapper>
        <CardContent className="p-6 space-y-6" style={{ ...CONTENT_STYLE, alignItems: 'center', justifyContent: 'center' }}>
          <LoadingSpinner />
        </CardContent>
      </AnalyticsCardWrapper>
    );
  }

  if (shouldShowCompletionMetric) {
    return (
      <AnalyticsCardWrapper>
        <CardContent className="p-6 space-y-6" style={CONTENT_STYLE}>
          <div>
            <AssessmentCompletionMetric />
          </div>
          {isTeamLeader && teamId && (
            <div>
              <SurveyInProgressWidget teamId={teamId} />
            </div>
          )}
        </CardContent>
      </AnalyticsCardWrapper>
    );
  }

  if (noTeamData) {
    return (
      <AnalyticsCardWrapper>
        <CardContent className="p-6 space-y-6" style={CONTENT_STYLE}>
          <div className="p-8 text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Team Culture Analytics Not Available</h3>
            {isTeamLeader ? (
              <>
                <p className="text-gray-600 mb-2">
                  You are a team leader, but there is no team culture data available for this assessment period.
                </p>
                <p className="text-gray-600 mb-2">This could mean:</p>
                <ul className="text-gray-600 text-left inline-block mb-3">
                  <li>• You haven't been assigned to a team for this assessment period</li>
                  <li>• Your team hasn't completed any surveys yet</li>
                  <li>• The assessment period hasn't started</li>
                </ul>
                <p className="text-gray-600">
                  Please check back later or contact your administrator to assign you to a team.
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-2">
                  You are not a team leader for this assessment period.
                </p>
                <p className="text-gray-600">
                  Switch to <strong>Organization</strong> view to see organization-level analytics.
                </p>
              </>
            )}
          </div>
        </CardContent>
      </AnalyticsCardWrapper>
    );
  }

  // If dashboard card data hasn't loaded yet (it can come in slightly later), show spinner rather than flash empty UI
  if (!cardsLoaded) {
    return (
      <AnalyticsCardWrapper>
        <CardContent className="p-6 space-y-6" style={{ ...CONTENT_STYLE, alignItems: 'center', justifyContent: 'center' }}>
          <LoadingSpinner />
        </CardContent>
      </AnalyticsCardWrapper>
    );
  }

  const renderDashboardContent = () => (
    <div className="space-y-6">
      {/* Row 1: Score Gauge (left) + Team vs Org Chart (right) */}
      <div className="analytics-equalize-row min-h-[360px]">
        <div className="col w-full max-w-md flex-shrink-0">
          <ScoreGaugeCard
            overallScore={overallScore}
            differential={differential}
            previousScore={previousScore}
            aiSummary={aiSummary}
            assessmentPeriodId={assessmentPeriodId!}
            teamId={teamId}
            employeeId={currentUserId ?? undefined}
            teamName={teamName}
            pillars={filteredPillars}
          />
        </div>
        <div className="col flex-1 min-w-0">
          <ScoresChart
            pillars={filteredPillars}
            teamId={teamId}
            assessmentPeriodId={assessmentPeriodId}
            teamName={teamName}
          />
        </div>
      </div>

    </div>
  );

  return (
    <AnalyticsCardWrapper>
      <CardContent className="p-6 space-y-6" style={CONTENT_STYLE}>
        {renderDashboardContent()}
      </CardContent>
    </AnalyticsCardWrapper>
  );
};



